import React, { useState, useRef } from 'react';
import { Plus, Upload, X, Loader2, CheckCircle, Trash2, Calendar } from 'lucide-react';
import { seriesApi, companyApi } from '../lib/api';
import { toast } from 'sonner';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const AddSeasonDialog = ({ open, onOpenChange, seriesId, seriesTitle, existingSeasons = [], onSeasonAdded }) => {
  const nextSeason = existingSeasons.length > 0 ? Math.max(...existingSeasons) + 1 : 1;
  const [episodes, setEpisodes] = useState([]);
  const [epForm, setEpForm] = useState({ title: '', description: '', video_file: '', videoFileName: '', thumbnail: '', thumbnailPreview: '', release_date: '' });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStep, setUploadStep] = useState(null);
  const [uploadingThumb, setUploadingThumb] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const videoRef = useRef(null);
  const thumbRef = useRef(null);

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadProgress(0);
    setUploadStep(null);
    try {
      const result = await companyApi.uploadVideo(file, (pct, step) => {
        setUploadProgress(pct);
        if (step) setUploadStep(step);
      });
      setEpForm(p => ({ ...p, video_file: result.url, videoFileName: file.name, duration: result.duration || 0 }));
      toast.success('Video uploaded');
    } catch (err) { toast.error(err.message || 'Upload failed'); }
    finally { setUploading(false); setUploadProgress(0); setUploadStep(null); }
  };

  const handleThumbUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingThumb(true);
    try {
      const preview = URL.createObjectURL(file);
      setEpForm(p => ({ ...p, thumbnailPreview: preview }));
      const result = await companyApi.uploadThumbnail(file);
      setEpForm(p => ({ ...p, thumbnail: result.url }));
      toast.success('Thumbnail uploaded');
    } catch (err) { toast.error(err.message || 'Upload failed'); }
    finally { setUploadingThumb(false); }
  };

  const addEpisode = () => {
    if (!epForm.title) { toast.error('Episode title is required'); return; }
    if (!epForm.video_file) { toast.error('Upload a video for the episode'); return; }
    setEpisodes(prev => [...prev, {
      episode_number: prev.length + 1,
      title: epForm.title,
      description: epForm.description,
      video_file: epForm.video_file,
      thumbnail: epForm.thumbnail,
      duration: epForm.duration || 0,
      release_date: epForm.release_date || undefined,
    }]);
    setEpForm({ title: '', description: '', video_file: '', videoFileName: '', thumbnail: '', thumbnailPreview: '', release_date: '' });
    toast.success(`Episode ${episodes.length + 1} added`);
  };

  const removeEpisode = (idx) => {
    setEpisodes(prev => prev.filter((_, i) => i !== idx).map((ep, i) => ({ ...ep, episode_number: i + 1 })));
  };

  const handleSubmit = async () => {
    if (episodes.length === 0) { toast.error('Add at least one episode'); return; }
    setSubmitting(true);
    try {
      const result = await seriesApi.addSeason(seriesId, { episodes });
      const sn = result.season_number || nextSeason;
      toast.success(`Season ${sn} added with ${episodes.length} episodes! Episodes are pending review.`);
      onSeasonAdded?.();
      onOpenChange(false);
      setEpisodes([]);
    } catch (err) { toast.error(err.message || 'Failed to add season'); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col bg-[#0F0B1E] border-white/10 p-0" data-testid="add-season-dialog">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-white/10 shrink-0">
          <DialogTitle className="text-lg font-bold">Add Season {nextSeason} to "{seriesTitle}"</DialogTitle>
          <p className="text-sm text-gray-400 mt-1">New episodes will be submitted for owner review before going live.</p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4" style={{ maxHeight: '55vh' }}>
          {/* Added episodes */}
          {episodes.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs text-gray-400 font-medium">{episodes.length} episode(s) ready</p>
              {episodes.map((ep, i) => (
                <div key={i} className="flex items-center gap-3 p-2.5 bg-[#030014] rounded-lg border border-white/5">
                  <span className="text-xs font-bold text-[#00FF9D] w-8">E{ep.episode_number}</span>
                  <span className="flex-1 text-sm truncate">{ep.title}</span>
                  <span className="text-xs text-[#00FF9D]">Uploaded</span>
                  <button onClick={() => removeEpisode(i)} className="text-red-400 hover:text-red-300 p-1">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Episode form */}
          <div className="space-y-3 p-4 rounded-lg border border-[#00FF9D]/20 bg-[#00FF9D]/5">
            <p className="text-sm font-semibold text-[#00FF9D]">Add Episode</p>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Title *</label>
              <Input value={epForm.title} onChange={e => setEpForm(p => ({ ...p, title: e.target.value }))}
                placeholder="Episode title" className="bg-[#030014] border-[#8B5CF6]/20 h-9" data-testid="new-season-ep-title" />
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Description</label>
              <Textarea value={epForm.description} onChange={e => setEpForm(p => ({ ...p, description: e.target.value }))}
                placeholder="Episode description" className="bg-[#030014] border-[#8B5CF6]/20 min-h-[60px]" data-testid="new-season-ep-desc" />
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Video *</label>
              {epForm.video_file ? (
                <div className="flex items-center gap-2 p-2 bg-[#00FF9D]/10 border border-[#00FF9D]/20 rounded-lg">
                  <CheckCircle className="w-4 h-4 text-[#00FF9D]" />
                  <span className="text-xs text-[#00FF9D] flex-1 truncate">{epForm.videoFileName}</span>
                  <button onClick={() => setEpForm(p => ({ ...p, video_file: '', videoFileName: '' }))} className="p-0.5"><X className="w-3 h-3 text-gray-400" /></button>
                </div>
              ) : uploading ? (
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 text-[#00FF9D] animate-spin" />
                    <span className="text-xs">
                      {uploadStep && uploadStep !== 'queued'
                        ? `Optimizing... (${uploadStep})`
                        : uploadProgress >= 96
                          ? 'Optimizing for streaming...'
                          : `${uploadProgress}%`}
                    </span>
                  </div>
                  <div className="w-full h-1.5 bg-gray-800 rounded-full"><div className="h-full bg-[#00FF9D] rounded-full transition-all" style={{ width: `${uploadProgress}%` }} /></div>
                </div>
              ) : (
                <>
                  <input ref={videoRef} type="file" accept="video/*" className="hidden" onChange={handleVideoUpload} />
                  <Button type="button" variant="outline" size="sm" onClick={() => videoRef.current?.click()} className="border-[#00FF9D]/30 text-[#00FF9D]" data-testid="new-season-ep-video-btn">
                    <Upload className="w-3 h-3 mr-1" /> Upload Video
                  </Button>
                </>
              )}
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Thumbnail (optional)</label>
              {epForm.thumbnailPreview && <img src={epForm.thumbnailPreview} alt="" className="w-32 h-20 object-cover rounded mb-1 border border-gray-800" />}
              <input ref={thumbRef} type="file" accept="image/*" className="hidden" onChange={handleThumbUpload} />
              <Button type="button" variant="outline" size="sm" onClick={() => thumbRef.current?.click()} disabled={uploadingThumb} className="border-gray-700">
                {uploadingThumb ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />} Thumbnail
              </Button>
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block flex items-center gap-1"><Calendar className="w-3 h-3" /> Release Date <span className="text-gray-600">(optional)</span></label>
              <Input type="date" value={epForm.release_date} onChange={e => setEpForm(p => ({ ...p, release_date: e.target.value }))}
                min={new Date().toISOString().split('T')[0]} className="bg-[#030014] border-[#8B5CF6]/20 h-8 max-w-[160px] text-sm" data-testid="new-season-ep-date" />
            </div>
            <Button onClick={addEpisode} className="w-full bg-[#00FF9D]/20 text-[#00FF9D] hover:bg-[#00FF9D]/30" data-testid="new-season-add-ep-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Episode
            </Button>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-white/10 flex gap-3 justify-end shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-white/10">Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting || episodes.length === 0} className="btn-primary" data-testid="submit-season-btn">
            {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
            Submit Season {nextSeason} ({episodes.length} ep)
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddSeasonDialog;
